import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';
import { validate, symptomEntrySchema } from '../utils/validators';
import auditService from './auditService';
import encryptionService from './encryptionService';

/**
 * Service layer for symptom entries
 * Handles all database operations for symptom tracking
 */

export const symptomService = {
  /**
   * Verify auth session is fully established
   * Checks if auth.uid() is available in database context
   * @returns {Promise<{data: {sessionValid: boolean}, error}>}
   */
  async verifySession() {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        return { 
          data: { sessionValid: false }, 
          error: { message: 'Not authenticated' } 
        };
      }
      
      // Verify auth.uid() is available by attempting a simple query
      // This will fail if auth.uid() returns null in RLS policy context
      const { data, error } = await supabase?.from('user_profiles')?.select('id')?.eq('id', user?.id)?.single();
      
      if (error) {
        console.warn('Session verification query failed:', error);
        return { 
          data: { sessionValid: false }, 
          error: { message: 'Session not ready', details: error } 
        };
      }
      
      // If query succeeds, auth.uid() is available
      return { 
        data: { sessionValid: true }, 
        error: null 
      };
    } catch (error) {
      console.error('Session verification error:', error);
      return { 
        data: { sessionValid: false }, 
        error: { message: 'Verification failed', details: error } 
      };
    }
  },

  /**
   * Create a new symptom entry
   * @param {object} entryData - Symptom entry data
   * @returns {Promise<{data, error}>}
   */
  async createEntry(entryData) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    // ===== DIAGNOSTIC LOGGING START =====
    console.group('🔍 Daily Check-In Save - Diagnostic Logging');
    console.log('📥 Raw Entry Data Received:', {
      managedProfileId: entryData?.managedProfileId,
      feelingLevel: entryData?.feelingLevel,
      symptomCategories: entryData?.symptomCategories,
      hasEncryptionPassword: !!entryData?.encryptionPassword,
      timestamp: new Date()?.toISOString()
    });
    // ===== DIAGNOSTIC LOGGING END =====
    
    // Validate input
    const validation = validate(symptomEntrySchema, entryData);
    if (!validation?.success) {
      console.error('❌ Validation Failed:', validation?.errors);
      console.groupEnd();
      logger?.warn('Invalid symptom entry data', { requestId, errors: validation?.errors });
      return { data: null, error: { message: 'Invalid data', details: validation?.errors } };
    }
    
    console.log('✅ Validation Passed');
    
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError) {
        console.error('❌ Failed to get user:', userError);
        console.groupEnd();
        return { data: null, error: { message: 'Authentication error', details: userError } };
      }
      
      if (!user) {
        console.error('❌ User Not Authenticated');
        console.groupEnd();
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      console.log('👤 Authenticated User:', {
        userId: user?.id,
        email: user?.email
      });
      
      // Map daily check-in categories to database enum values
      const categoryMapping = {
        'pain': 'musculoskeletal',
        'headache': 'head_neck',
        'migraine': 'head_neck',
        'fatigue': 'neurological',
        'nausea': 'digestive',
        'dizziness': 'neurological',
        'breathing': 'respiratory',
        'chest': 'cardiovascular',
        'stomach': 'digestive',
        'skin': 'skin',
        'anxiety': 'neurological',
        'sleep': 'neurological'
      };
      
      // Handle "feeling good" entries - use 'other' category with appropriate symptoms
      const isGoodFeeling = entryData?.feelingLevel === 'good';
      
      // Get first category and map it to database enum, default to 'other'
      const firstCategory = entryData?.symptomCategories?.[0];
      const dbCategory = firstCategory ? (categoryMapping?.[firstCategory] || 'other') : 'other';
      
      // Ensure symptoms array is never empty (database constraint)
      // For "feeling good" entries, use a special marker
      const symptomsArray = isGoodFeeling 
        ? ['feeling_good'] 
        : (entryData?.symptomCategories?.length > 0 
            ? entryData?.symptomCategories 
            : ['general_discomfort']);
      
      const entry = {
        user_id: user?.id,
        // Add managed_profile_id if provided (for caregiver submissions)
        managed_profile_id: entryData?.managedProfileId || null,
        // Required legacy fields
        category: dbCategory,
        symptoms: symptomsArray,
        severity: entryData?.severity >= 7 ? 'severe' : entryData?.severity >= 4 ? 'moderate' : 'mild',
        // Daily check-in fields (matching database schema exactly)
        feeling_level: entryData?.feelingLevel,
        symptom_categories: entryData?.symptomCategories || [],
        other_symptom_text: entryData?.otherSymptomText || null,
        body_locations: entryData?.bodyLocations || [],
        other_location_text: entryData?.otherLocationText || null,
        severity_numeric: entryData?.severity || 1,
        timing: entryData?.timing || null,
        triggers: entryData?.triggers || [],
        custom_trigger: entryData?.customTrigger || null,
        daily_impacts: entryData?.dailyImpacts || [],
        medication_status: entryData?.medicationStatus || null,
        medication_details: entryData?.medicationDetails || null,
        notes: entryData?.notes || null,
        photos: entryData?.photos && entryData?.photos?.length > 0 ? entryData?.photos : [],
        symptom_date: new Date()?.toISOString()?.split('T')?.[0],
        symptom_time: new Date()?.toTimeString()?.split(' ')?.[0],
        created_at: new Date()?.toISOString()
      };
      
      // ===== DIAGNOSTIC LOGGING: Entry Object =====
      console.log('📝 Entry Object Before Encryption:', {
        user_id: entry?.user_id,
        managed_profile_id: entry?.managed_profile_id,
        category: entry?.category,
        symptoms: entry?.symptoms,
        severity: entry?.severity,
        feeling_level: entry?.feeling_level,
        symptom_categories: entry?.symptom_categories,
        body_locations: entry?.body_locations,
        severity_numeric: entry?.severity_numeric,
        symptom_date: entry?.symptom_date,
        symptom_time: entry?.symptom_time
      });
      
      if (entry?.managed_profile_id) {
        console.warn('🔔 CAREGIVER SUBMISSION DETECTED:', {
          caregiverUserId: entry?.user_id,
          managedProfileId: entry?.managed_profile_id,
          submissionType: 'dependent_entry'
        });
      } else {
        console.log('ℹ️ Self-submission (no managed_profile_id)');
      }
      // ===== END DIAGNOSTIC LOGGING =====
      
      // Encrypt sensitive fields if encryption password is available
      let finalEntry = entry;
      if (entryData?.encryptionPassword) {
        const { data: encryptedEntry, error: encryptError } = encryptionService?.encryptSymptomEntry(
          entry,
          entryData?.encryptionPassword
        );
        
        if (encryptError) {
          logger?.warn('Failed to encrypt entry, saving unencrypted', { requestId, error: encryptError });
        } else {
          finalEntry = encryptedEntry;
          logger?.debug('Entry encrypted successfully', { requestId });
        }
      }
      
      logger?.debug('Creating symptom entry', { requestId, entry: finalEntry });
      
      // ===== DIAGNOSTIC LOGGING: Database INSERT =====
      console.log('💾 Attempting Database INSERT...');
      console.log('📤 Final Entry to Insert:', {
        user_id: finalEntry?.user_id,
        managed_profile_id: finalEntry?.managed_profile_id,
        managed_profile_id_type: typeof finalEntry?.managed_profile_id,
        managed_profile_id_is_null: finalEntry?.managed_profile_id === null,
        managed_profile_id_is_undefined: finalEntry?.managed_profile_id === undefined,
        managed_profile_id_value: JSON.stringify(finalEntry?.managed_profile_id),
        category: finalEntry?.category,
        symptoms: finalEntry?.symptoms,
        severity: finalEntry?.severity,
        isEncrypted: finalEntry !== entry
      });
      
      // CRITICAL VALIDATION: Ensure managed_profile_id is explicitly null or a valid UUID
      if (finalEntry?.managed_profile_id !== null && finalEntry?.managed_profile_id !== undefined) {
        // Validate it's a proper UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex?.test(finalEntry?.managed_profile_id)) {
          console.error('❌ INVALID managed_profile_id FORMAT:', {
            value: finalEntry?.managed_profile_id,
            type: typeof finalEntry?.managed_profile_id,
            expectedFormat: 'UUID or null'
          });
          console.groupEnd();
          return {
            data: null,
            error: {
              message: 'Invalid profile selection. Please refresh the page and try again.',
              action: 'REFRESH_PROFILES',
              technicalDetails: `Invalid managed_profile_id format: ${finalEntry?.managed_profile_id}`,
              code: 'INVALID_PROFILE_ID'
            }
          };
        }
      }
      
      console.log('✅ managed_profile_id validation passed');
      // ===== END DIAGNOSTIC LOGGING =====
      
      const { data, error } = await supabase?.from('symptom_entries')?.insert([finalEntry])?.select()?.single();
      
      if (error) {
        // ===== DIAGNOSTIC LOGGING: Database Error =====
        console.error('❌ DATABASE INSERT FAILED:', {
          errorMessage: error?.message,
          errorCode: error?.code,
          errorDetails: error?.details,
          errorHint: error?.hint,
          fullError: error,
          attemptedEntry: {
            user_id: finalEntry?.user_id,
            managed_profile_id: finalEntry?.managed_profile_id,
            category: finalEntry?.category
          }
        });
        console.groupEnd();
        // ===== END DIAGNOSTIC LOGGING =====
        
        logger?.error('Failed to create symptom entry', error, { requestId, userId: user?.id, entry });
        
        // ===== ENHANCED ERROR HANDLING: User-Friendly Messages =====
        let userMessage = 'Failed to save your entry';
        let actionRequired = null;
        
        // 1. Authentication/Session Errors
        if (error?.code === 'PGRST301' || error?.message?.includes('JWT') || error?.message?.includes('session')) {
          userMessage = 'Your session has expired. Please sign in again to save your check-in.';
          actionRequired = 'REAUTHENTICATE';
          console.warn('🔐 Session expired - user needs to re-authenticate');
        }
        // 2. RLS Permission Denied (Profile Access Issues)
        else if (error?.code === '42501' || error?.message?.includes('permission denied') || error?.message?.includes('RLS')) {
          if (finalEntry?.managed_profile_id) {
            userMessage = 'Unable to save check-in for this profile. The profile may no longer be accessible or has been removed.';
            actionRequired = 'REFRESH_PROFILES';
            console.warn('🚫 RLS policy blocked caregiver submission - managed_profile_id may be invalid');
          } else {
            userMessage = 'Permission denied. Please check your account status or contact support.';
            actionRequired = 'CONTACT_SUPPORT';
            console.warn('🚫 RLS policy blocked self-submission - unexpected permission issue');
          }
        }
        // 3. Missing Required Fields (Database Constraints)
        else if (error?.code === '23502' || error?.message?.includes('null value') || error?.message?.includes('violates not-null')) {
          const missingField = error?.message?.match(/column "(\w+)"/)?.[1] || 'required field';
          userMessage = `Missing required information: ${missingField}. Please fill in all required fields and try again.`;
          actionRequired = 'CHECK_FORM';
          console.warn(`⚠️ Missing required field: ${missingField}`);
        }
        // 4. Foreign Key Constraint Violations (Invalid managed_profile_id)
        else if (error?.code === '23503' || error?.message?.includes('foreign key') || error?.message?.includes('violates foreign key constraint')) {
          userMessage = 'The selected profile is no longer available. Please refresh and select a valid profile.';
          actionRequired = 'REFRESH_PROFILES';
          console.warn('🔗 Foreign key violation - managed_profile_id does not exist in managed_profiles table');
        }
        // 5. Network Timeout Errors
        else if (error?.message?.includes('timeout') || error?.message?.includes('network') || error?.code === 'ECONNABORTED') {
          userMessage = 'Network connection timeout. Please check your internet connection and try again.';
          actionRequired = 'RETRY';
          console.warn('🌐 Network timeout detected');
        }
        // 6. Check Constraint Violations
        else if (error?.code === '23514' || error?.message?.includes('check constraint')) {
          userMessage = 'Invalid data format. Please review your entries and ensure all values are within acceptable ranges.';
          actionRequired = 'CHECK_FORM';
          console.warn('⚠️ Check constraint violation - data validation failed at database level');
        }
        // 7. Unique Constraint Violations (Duplicate Entries)
        else if (error?.code === '23505' || error?.message?.includes('unique constraint') || error?.message?.includes('duplicate')) {
          userMessage = 'A check-in for this time already exists. Please wait a moment before submitting again.';
          actionRequired = 'WAIT';
          console.warn('🔄 Duplicate entry detected');
        }
        // 8. Generic Database Errors
        else if (error?.code?.startsWith('P') || error?.code?.startsWith('23')) {
          userMessage = 'Database error occurred. Please try again or contact support if the issue persists.';
          actionRequired = 'RETRY';
          console.warn('💾 Generic database error:', error?.code);
        }
        // 9. Fallback for Unknown Errors
        else {
          userMessage = error?.message || 'Failed to save your entry. Please try again.';
          actionRequired = 'RETRY';
          console.warn('❓ Unknown error type:', error);
        }
        
        console.log('📢 User-Friendly Error Message:', userMessage);
        console.log('🎯 Recommended Action:', actionRequired);
        // ===== END ENHANCED ERROR HANDLING =====
        
        return { 
          data: null, 
          error: { 
            message: userMessage,
            action: actionRequired,
            technicalDetails: error?.message,
            code: error?.code,
            details: error 
          } 
        };
      }
      
      // ===== DIAGNOSTIC LOGGING: Success =====
      console.log('✅ DATABASE INSERT SUCCESSFUL:', {
        entryId: data?.id,
        user_id: data?.user_id,
        managed_profile_id: data?.managed_profile_id,
        created_at: data?.created_at,
        symptom_date: data?.symptom_date
      });
      console.groupEnd();
      // ===== END DIAGNOSTIC LOGGING =====
      
      logger?.audit('SYMPTOM_ENTRY_CREATED', user?.id, { requestId, entryId: data?.id });
      
      // Log to audit trail (trigger will also log, but this adds metadata)
      await auditService?.logEvent({
        actionType: 'CREATE',
        resourceType: 'SYMPTOM_ENTRY',
        resourceId: data?.id,
        changes: { new: data },
        metadata: { source: 'symptom_service', requestId }
      });
      
      return { data, error: null };
    } catch (error) {
      logger?.error('Symptom entry creation error', error, { requestId });
      return { data: null, error: { message: error?.message || 'Failed to create entry', details: error } };
    }
  },
  
  /**
   * Get symptom entries for current user
   * @param {object} options - Query options (limit, offset, dateRange, decryptionPassword)
   * @returns {Promise<{data, error}>}
   */
  async getEntries(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      let query = supabase?.from('symptom_entries')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });
      
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }
      
      if (options?.offset) {
        query = query?.range(options?.offset, options?.offset + (options?.limit || 10) - 1);
      }
      
      if (options?.dateRange) {
        query = query?.gte('created_at', options?.dateRange?.start)?.lte('created_at', options?.dateRange?.end);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger?.error('Failed to fetch symptom entries', error, { requestId, userId: user?.id });
        return { data: null, error };
      }
      
      // AUTOMATIC DECRYPTION: Decrypt all encrypted entries
      let finalData = data;
      if (data?.length > 0) {
        finalData = data?.map(entry => {
          if (entry?.encryption_metadata) {
            const { data: decryptedEntry } = encryptionService?.decryptSymptomEntryAutomatic(entry);
            return decryptedEntry || entry;
          }
          return entry;
        });
      }
      
      logger?.debug('Symptom entries fetched and decrypted', { requestId, count: finalData?.length });
      return { data: finalData, error: null };
    } catch (error) {
      logger?.error('Symptom entries fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch entries' } };
    }
  },
  
  /**
   * Update a symptom entry
   * @param {string} entryId - Entry ID
   * @param {object} updates - Fields to update
   * @returns {Promise<{data, error}>}
   */
  async updateEntry(entryId, updates) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Get old data before update
      const { data: oldData } = await supabase?.from('symptom_entries')?.select('*')?.eq('id', entryId)?.eq('user_id', user?.id)?.single();
      
      const { data, error } = await supabase?.from('symptom_entries')?.update(updates)?.eq('id', entryId)?.eq('user_id', user?.id)?.select()?.single();
      
      if (error) {
        logger?.error('Failed to update symptom entry', error, { requestId, entryId, userId: user?.id });
        return { data: null, error };
      }
      
      logger?.audit('SYMPTOM_ENTRY_UPDATED', user?.id, { requestId, entryId });
      
      // Log to audit trail with before/after
      await auditService?.logEvent({
        actionType: 'UPDATE',
        resourceType: 'SYMPTOM_ENTRY',
        resourceId: entryId,
        changes: { old: oldData, new: data },
        metadata: { source: 'symptom_service', requestId, updatedFields: Object.keys(updates) }
      });
      
      return { data, error: null };
    } catch (error) {
      logger?.error('Symptom entry update error', error, { requestId, entryId });
      return { data: null, error: { message: 'Failed to update entry' } };
    }
  },
  
  /**
   * Delete a symptom entry
   * @param {string} entryId - Entry ID
   * @returns {Promise<{data, error}>}
   */
  async deleteEntry(entryId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      // Get data before deletion
      const { data: oldData } = await supabase?.from('symptom_entries')?.select('*')?.eq('id', entryId)?.eq('user_id', user?.id)?.single();
      
      const { error } = await supabase?.from('symptom_entries')?.delete()?.eq('id', entryId)?.eq('user_id', user?.id);
      
      if (error) {
        logger?.error('Failed to delete symptom entry', error, { requestId, entryId, userId: user?.id });
        return { data: null, error };
      }
      
      logger?.audit('SYMPTOM_ENTRY_DELETED', user?.id, { requestId, entryId });
      
      // Log to audit trail
      await auditService?.logEvent({
        actionType: 'DELETE',
        resourceType: 'SYMPTOM_ENTRY',
        resourceId: entryId,
        changes: { old: oldData },
        metadata: { source: 'symptom_service', requestId }
      });
      
      return { data: { success: true }, error: null };
    } catch (error) {
      logger?.error('Symptom entry deletion error', error, { requestId, entryId });
      return { data: null, error: { message: 'Failed to delete entry' } };
    }
  }
};

export default symptomService;